import { t } from '@/i18n';
import {
  FALLBACK_TEXT,
  type FormatValueByUnitOptions,
  formatAmountNumberByUnit,
  formatNumber,
  handleZeroValueWithNegativeCount,
} from '@/utils';

type Option = FormatValueByUnitOptions & {
  rawValue?: number | null;
};

export const formatAmountReport = (
  value: number | null | undefined,
  type: 'csv' | 'pdf',
  option?: Option,
) => {
  if (value === null || value === undefined) {
    return type === 'csv' ? '' : FALLBACK_TEXT;
  }
  return type === 'csv'
    ? formatAmountForCsv(value, option)
    : formatAmountForPdf(value, option);
};

export const formatAmountForPdf = (
  value: number | null | undefined,
  option?: Option,
) => {
  if (value === null || value === undefined) {
    return FALLBACK_TEXT;
  }
  const { isDebugMode, negativeCount, rawValue } = option ?? {};
  if (typeof negativeCount === 'number' && negativeCount >= 1) {
    // 既存ロジック: negativeCount >= 1 かつ value === 0 の場合、フォールバック表示
    if (value === 0) {
      const { isAggregateValueZero, fallbackValue } =
        handleZeroValueWithNegativeCount(value, {
          negativeCount,
          isDebugMode,
        });
      if (isAggregateValueZero) {
        if (rawValue && rawValue < 1000) {
          // NOTE: rawValueが1000未満の場合は、--ではなく0を返す
          return '0';
        }
        return fallbackValue;
      }
    }
  }

  // NOTE: Fractional processing is done on the back end, so no front end processing is required.
  const thousandAmount = formatAmountNumberByUnit(value, 'thousand');
  return formatNumber(thousandAmount, { useNegativeSign: true });
};

export const formatNumberForPdf = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }

  return formatNumber(value);
};

export const formatAmountForCsv = (
  amount: number | null | undefined,
  option?: FormatValueByUnitOptions,
) => {
  if (amount === null || amount === undefined) {
    return '';
  }
  const { isDebugMode, negativeCount } = option ?? {};

  if (typeof negativeCount === 'number') {
    const { isAggregateValueZero, fallbackValue } =
      handleZeroValueWithNegativeCount(amount, {
        negativeCount,
        isDebugMode,
      });
    if (isAggregateValueZero) {
      // NOTE: 集計に負の値が使用されている場合は、--ではなく空白を返す
      return isDebugMode ? fallbackValue : '';
    }
  }

  // NOTE: Fractional processing is done on the back end, so no front end processing is required.
  return formatAmountNumberByUnit(amount, 'thousand');
};

export const formatExistenceValue = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }
  return value
    ? t('report-standard-fund-existence-true')
    : t('report-standard-fund-existence-false');
};

export const formatProjectStartYearForPdf = (
  value: number | null | undefined,
) => {
  return value ?? t('unknown-start');
};

export const formatProjectEndYearForPdf = (
  value: number | null | undefined,
) => {
  return value ?? t('no-scheduled-end');
};
